import { FC, useCallback, useEffect, useState } from 'react';

import { useBreakpoint } from '@contexts/breakpoints';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';

import { YearTabProps } from './interface';
import { getCurrentYearTab, getNextYearTab } from './helpers';
import MobileSearchMonthList from './MobileSearchMonth';
import DesktopSearchMonth from './DesktopSearchMonth';

export interface SearchMonthDateProps {
  onMonthsUpdated: (months: YearTabProps[]) => void;
  inputComponent: 'shopAndFly' | 'setur';
}

const SearchMonthDate: FC<SearchMonthDateProps> = ({
  onMonthsUpdated,
  inputComponent,
}) => {
  const { xl: isDesktop } = useBreakpoint();
  const query = useSearchParamsToQuery();

  const [selectedMonthsLabel, setSelectedMonthsLabel] = useState('');
  const [currentYearTab, setCurrentYearTab] = useState<YearTabProps>({
    year: getCurrentYearTab().year,
    months: [...getCurrentYearTab().months],
  });
  const [nextYearTab, setNextYearTab] = useState<YearTabProps>({
    year: getNextYearTab().year,
    months: [...getNextYearTab().months],
  });

  useEffect(() => {
    const selectedItems: string[] = [];
    currentYearTab.months.forEach((item) => {
      if (item.checked) {
        selectedItems.push(`${item.abbreviation} ${currentYearTab.year}`);
      }
    });

    nextYearTab.months.forEach((item) => {
      if (item.checked) {
        selectedItems.push(`${item.abbreviation} ${nextYearTab.year}`);
      }
    });
    setSelectedMonthsLabel(selectedItems.join(','));

    onMonthsUpdated([currentYearTab, nextYearTab]);
  }, [currentYearTab, nextYearTab]);

  useEffect(() => {
    const dateQuery = String(query?.date);
    if (!dateQuery || dateQuery === 'undefined') {
      resetSearchMonthDateState();
      return;
    }

    const checkedDates = dateQuery.split?.(',');

    setCurrentYearTab((val) => ({
      ...val,
      months: val.months.map((month) => {
        let matchMonth = { ...month };
        checkedDates.forEach((date) => {
          if (`${val.year}${month.number}` === date)
            matchMonth = { ...matchMonth, checked: true };
        });

        return matchMonth;
      }),
    }));

    setNextYearTab((val) => ({
      ...val,
      months: val.months.map((month) => {
        let matchMonth = { ...month };
        checkedDates.forEach((date) => {
          if (`${val.year}${month.number}` === date)
            matchMonth = { ...matchMonth, checked: true };
        });

        return matchMonth;
      }),
    }));
  }, [query]);

  const resetSearchMonthDateState = useCallback(() => {
    setCurrentYearTab({
      year: getCurrentYearTab().year,
      months: [...getCurrentYearTab().months],
    });
    setNextYearTab({
      year: getNextYearTab().year,
      months: [...getNextYearTab().months],
    });
    setSelectedMonthsLabel('');
  }, []);

  return isDesktop || isDesktop === undefined ? (
    <DesktopSearchMonth
      currentYearTab={currentYearTab}
      nextYearTab={nextYearTab}
      setCurrentYearTab={setCurrentYearTab}
      setNextYearTab={setNextYearTab}
      selectedMonthsLabel={selectedMonthsLabel}
      inputComponent={inputComponent}
    />
  ) : (
    <MobileSearchMonthList
      currentYearTab={currentYearTab}
      nextYearTab={nextYearTab}
      setCurrentYearTab={setCurrentYearTab}
      setNextYearTab={setNextYearTab}
      selectedMonthsLabel={selectedMonthsLabel}
      inputComponent={inputComponent}
    />
  );
};

export default SearchMonthDate;
